import React, { useEffect, useMemo, useState } from 'react';
import { Copy, ExternalLink, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { bookingApi } from '../../api/bookingApi';
import { ClientLayout } from '../../components/layouts';
import { formatClientDate, formatClientMoney, getClientStatusMeta } from './clientHelpers';

const ClientBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    let cancelled = false;
    bookingApi.myBookings()
      .then((response) => { if (!cancelled) setBookings(response.data || []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || "Couldn't load your bookings."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings.filter((booking) => {
      const matchesStatus = status === 'all' || booking.status === status;
      const haystack = `${booking.service?.name || ''} ${booking.gigger?.name || ''} ${booking.trackingId || ''}`.toLowerCase();
      return matchesStatus && (!q || haystack.includes(q));
    });
  }, [bookings, query, status]);

  const copyTracking = async (trackingId) => {
    try {
      await navigator.clipboard.writeText(trackingId);
      setCopied(trackingId);
      window.setTimeout(() => setCopied(''), 1200);
    } catch (_) {}
  };

  return (
    <ClientLayout title="My Bookings">
      <div className="mx-auto w-full max-w-[1400px]">
        <div className="mb-6">
          <h2 className="m-0 font-nunito text-[clamp(1.6rem,3vw,2.15rem)] font-black text-giggs-ink">My bookings</h2>
          <p className="mb-0 mt-2 text-sm leading-6 text-giggs-muted">Review every booking, copy its tracking number, or open the live tracker.</p>
        </div>

        <div className="mb-5 flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/80 md:flex-row md:items-center">
          <label className="relative min-w-0 flex-1 md:max-w-md">
            <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search service, Gigger or tracking ID" className="box-border w-full rounded-xl border-0 bg-white py-3 pl-10 pr-4 text-sm text-giggs-ink ring-1 ring-inset ring-slate-200 outline-none focus:ring-2 focus:ring-giggs-green" />
          </label>
          <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-xl border-0 bg-white px-4 py-3 text-sm text-giggs-ink ring-1 ring-inset ring-slate-200 outline-none focus:ring-2 focus:ring-giggs-green">
            <option value="all">All statuses</option><option value="pending">Pending</option><option value="accepted">Accepted</option><option value="in_progress">In progress</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="lapsed">No response</option><option value="declined">Declined</option>
          </select>
          <Link to="/book" className="inline-flex min-h-11 items-center justify-center rounded-xl bg-giggs-green px-5 py-3 text-sm font-bold text-white no-underline hover:bg-giggs-green-hover">New booking</Link>
        </div>

        {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</div>}

        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80">
          {loading ? <div className="px-5 py-14 text-center text-sm text-giggs-muted">Loading bookings…</div> : filtered.length === 0 ? <div className="px-5 py-14 text-center text-sm text-giggs-muted">No bookings match your filters.</div> : (
            <div className="divide-y divide-slate-100">
              {filtered.map((booking) => {
                const meta = getClientStatusMeta(booking.status);
                return (
                  <article key={booking._id} className="grid gap-4 px-5 py-5 sm:px-6 lg:grid-cols-[1.4fr_1fr_auto] lg:items-center">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2"><h3 className="m-0 text-base font-bold text-giggs-ink">{booking.service?.name || 'Giggs booking'}</h3><span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset ${meta.className}`}>{meta.label}</span></div>
                      <p className="mb-0 mt-2 text-sm text-giggs-muted">{booking.gigger?.name || 'Gigger pending'} · {formatClientDate(booking.scheduledDate)} · {booking.startTime || '—'}–{booking.endTime || '—'}</p>
                    </div>
                    <div className="min-w-0 rounded-xl bg-slate-50 px-3.5 py-3">
                      <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Tracking number</p>
                      <div className="mt-1 flex items-center gap-2"><span className="min-w-0 flex-1 break-all font-mono text-xs font-semibold text-slate-700">{booking.trackingId}</span><button type="button" onClick={() => copyTracking(booking.trackingId)} className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-white text-giggs-green ring-1 ring-slate-200" title="Copy tracking number"><Copy size={14} /></button></div>
                      {copied === booking.trackingId && <p className="mb-0 mt-1 text-[10px] font-semibold text-giggs-green">Copied</p>}
                    </div>
                    <div className="flex items-center justify-between gap-4 lg:flex-col lg:items-end">
                      <span className="font-nunito text-lg font-black text-giggs-ink">{formatClientMoney(booking.total)}</span>
                      <Link to={`/client/track/${booking.trackingId}`} className="inline-flex items-center gap-1.5 rounded-xl bg-giggs-dark px-4 py-2.5 text-xs font-bold text-white no-underline hover:bg-giggs-green">Track booking <ExternalLink size={13} /></Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientBookings;
